import { useEffect } from 'react';
import { useSetState } from 'minimal-shared/hooks';

import { debounce } from '@mui/material/utils';

import { useSearchParams } from 'src/routes/hooks';

import { getData, multipleQuery } from 'src/lib/firebase';

import { getOrderBy, getFilters, getProduct, promotionDataFormatter } from '../product-ssr';

// ----------------------------------------------------------------------

export function useGetPromotions(uid) {
  const searchParams = useSearchParams();

  const searchParamsSortBy = searchParams.get('sortBy') ?? 'newest';

  const { state, setState } = useSetState({
    index: 0,
    loading: true,
    allPromotions: [],
    lastVisible: null,
    loadingMore: false,
    pagination: { page: 0, pageSize: 0, totalOrders: 0, isLastPage: false },
  });

  const getPromotions = async (prevAllPromotions) => {
    const promotions = await getData({
      path: 'promotions',
      formatter: promotionDataFormatter,
      defaultData: [],
      options: {
        limit: 26,
        startAfter: state.lastVisible,
        orderBy: getOrderBy(searchParamsSortBy),
        where: [
          { field: 'sellerUID', operator: '==', value: uid },
          ...getFilters(searchParams, ['name', 'publish', 'priceSale', 'createdAt']),
        ],
      },
    });

    const total = promotions.length;

    setState({
      loading: false,
      loadingMore: false,
      index: prevAllPromotions.length + total,
      lastVisible: promotions[total - (total === 26 ? 2 : 1)],
      allPromotions: [...prevAllPromotions, ...promotions.slice(0, total === 26 ? -1 : total)],
      pagination: {
        ...state.pagination,
        totalOrders: state.pagination.totalOrders + total - 1,
        isLastPage:
          state.pagination.page ===
          Math.ceil((state.pagination.totalOrders + total - 1) / state.pagination.pageSize) - 1,
      },
    });
  };

  const loadMoreProducts = () => {
    setState({ loadingMore: true });
    getPromotions(state.allPromotions);
  };

  const advancedSearch = debounce(async (searchValue) => {
    setState({ loading: true });
    const promotion = await getProduct(searchValue);
    if (promotion.id) {
      setState({ index: 1, allPromotions: [promotion], loading: false });
    } else {
      const allPromotions = await multipleQuery({
        path: 'promotions',
        searchValue,
        fields: ['code', 'name'],
        formatter: promotionDataFormatter,
        mainFilters: [
          { field: 'sellerUID', operator: '==', value: uid },
          ...getFilters(searchParams, ['name', 'publish', 'priceSale', 'createdAt']),
        ],
      });
      setState({ allPromotions, loading: false, index: allPromotions.length });
    }
  }, 500);

  useEffect(() => {
    if (searchParams.get('query')) {
      advancedSearch(searchParams.get('query'));
    } else {
      setState({ loading: true, lastVisible: null });
      getPromotions([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid, searchParams]);

  return {
    ...state,
    loadMoreProducts,
    sortBy: searchParamsSortBy,
    products: state.allPromotions ?? [],
    empty: !state.loading && !state.allPromotions.length,
    setPagination: (pagination) => setState({ pagination }),
  };
}
